/** @file Assets row actions: menu items, create region, show in the selected slot, rename, focus. */
import { ids } from '@pose2d/format/ids';
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { deletable } from '../../forms/deletable';
import type { DeleteTarget } from '../../forms/deletePlan';
import { freeName } from '../../forms/freeName';
import type { RowAction, RowFocus } from '../../forms/rowAction';
import type { InspectorFocus } from '../../stores/focusStore';
import type { SelectionSlice } from '../../stores/types';
import type { MenuItem } from '../../ui-kit/ContextMenuList';

const isImage = (id: Id): boolean => ids.prefixOf(id) === 'img';
const calls = (list: CommandCall[], expand: Id | null, focus: RowFocus | null): RowAction => ({ kind: 'calls', calls: list, expand, focus });

/** skin.setEntry (default skin, placeholder = attachment name) unless already mapped, then slot.setAttachment. */
function showInSlot(doc: Doc, att: Id, slot: Id): CommandCall[] {
  const placeholder = doc.attachments[att]?.name ?? att;
  const mapped = doc.skins[doc.defaultSkin]?.entries.some((e) => e.slot === slot && e.placeholder === placeholder && e.attachment === att) ?? false;
  const entry: CommandCall[] = mapped ? [] : [{ name: 'skin.setEntry', input: { slot, placeholder, attachment: att } }];
  return [...entry, { name: 'slot.setAttachment', input: { target: slot, value: placeholder } }];
}

/** A delete request for row `id`, or null when the command would refuse it. */
function removeAction(doc: Doc, id: Id): RowAction | null {
  const target = assetActions.focusOf(id);
  return deletable(doc, target) ? { kind: 'delete', target } : null;
}

/** Row actions of the Assets panel (pure; rows are image or attachment ids). */
export const assetActions = {
  /** The Inspector focus of a row. */
  focusOf(id: Id): InspectorFocus {
    return { kind: isImage(id) ? 'image' : 'attachment', id };
  },
  /** Images and attachments are renamed inline. */
  canRename(id: Id): boolean {
    return ids.prefixOf(id) === 'img' || ids.prefixOf(id) === 'att';
  },
  /** image.rename or attachment.rename. */
  renameCall(id: Id, name: string): CommandCall {
    return { name: isImage(id) ? 'image.rename' : 'attachment.rename', input: { target: id, name } };
  },
  /** The focused row as a delete target (the Delete key); none when the focus is empty or the command would refuse it. */
  deleteTargets(doc: Doc, focus: InspectorFocus | null): DeleteTarget[] {
    return focus !== null && deletable(doc, focus) ? [focus] : [];
  },
  /** Menu items; deleting a used image and showing without exactly one selected slot are disabled. */
  items(doc: Doc, id: Id, selection: SelectionSlice, t: Translate): MenuItem[] {
    const first = isImage(id)
      ? { id: 'createRegion', label: t('panels.assets.createRegion') }
      : { id: 'showInSlot', label: t('panels.assets.showInSlot'), disabled: selection.slots.length !== 1 };
    const remove = { id: 'remove', label: t('panels.assets.remove'), danger: true, separatorBefore: true, disabled: !deletable(doc, assetActions.focusOf(id)) };
    return [first, { id: 'rename', label: t('panels.assets.rename') }, remove];
  },
  /** What choosing `item` on row `id` does. */
  action(doc: Doc, id: Id, item: string, selection: SelectionSlice): RowAction | null {
    if (item === 'rename') return { kind: 'rename' };
    if (item === 'remove') return removeAction(doc, id);
    const slot = selection.slots[0];
    if (item === 'showInSlot' && slot !== undefined && selection.slots.length === 1) return calls(showInSlot(doc, id, slot), null, null);
    if (item !== 'createRegion' || !isImage(id)) return null;
    const name = freeName(doc, 'attachment', doc.images[id]?.name ?? 'region');
    return calls([{ name: 'attachment.createRegion', input: { name, image: id } }], id, { kind: 'attachment', created: name });
  },
};
